import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import {
  PlusSmallIcon as PlusSmIcon,
  MagnifyingGlassIcon as SearchIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid";
import { useRouter } from "next/router";
import { XMarkIcon as XIcon } from "@heroicons/react/24/outline";
import debounce from "lodash/debounce";
import get from "lodash/get";
import InfiniteScroll from "react-infinite-scroll-component";
import Link from "next/link";
import React, { useRef, useEffect, useMemo } from "react";
import useSWR from "swr";

import { search, getSettingsForPage } from "../lib/api";
import Meta from "../components/meta";
import Spinner from "../components/spinner";

const SearchInput = ({ debouncedChangeHandler, initialValue }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <div className="rounded-md shadow-sm w-full sticky top-5">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
      </div>

      <input
        onChange={debouncedChangeHandler}
        ref={inputRef}
        id="search"
        defaultValue={initialValue}
        name="search"
        type="text"
        autoComplete="none"
        required
        autoFocus={true}
        className="w-full px-10 py-3 border border-gray-300 placeholder-gray-400 focus:ring-1 focus:ring-accent-500 focus:border-accent-500 rounded-md"
        placeholder="Search"
      />

      {initialValue ? (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          <XCircleIcon
            onClick={() => {
              inputRef.current.value = "";
              debouncedChangeHandler({ target: { value: "" } });
              inputRef.current.focus();
            }}
            className="cursor-pointer h-5 w-5 text-gray-400 hover:text-gray-900"
            aria-hidden="true"
          />
        </div>
      ) : null}
    </div>
  );
};

const Card = ({ data }) => {
  let { title, description, _type, name, bio, _id, slug } = data || {};

  if (!description && bio) description = bio;
  if (typeof description === "object") {
    description = get(description, "[0].children[0].text");
  }
  if (typeof description !== "string") {
    description = "";
  }

  let href = "";
  switch (_type) {
    case "project":
      href = `/projects/${slug.current}`;
      break;
    case "team":
      href = `/team?member=${slug.current}`;
      break;
    case "post":
      href = `/blog/${slug.current}`;
      break;
    case "video":
      href = `/video?video=${_id}`;
      break;
    case "pursuit":
      href = `/pursuits?pursuit=${_id}`;
      break;
    default:
      href = "/";
      break;
  }

  return (
    <Link href={href} className="group">
      <span className="inline-flex capitalize items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
        {_type}
      </span>
      <div className="text-base font-medium mt-4 group-hover:underline">
        {title || name}
      </div>
      <div className="text-sm line-clamp-3 mt-2 text-gray-500">
        {description}
      </div>
    </Link>
  );
};

const perPage = 30;
const LazyList = ({ data: initialData, page = 1, setPage }) => {
  if (!initialData || !initialData.length) return null;
  let data = [...initialData];

  if (data.length > perPage) {
    data = data.splice(0, page * perPage);
  }

  return (
    <InfiniteScroll
      className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:gap-x-8 xl:grid-cols-3"
      dataLength={data.length} //This is important field to render the next data
      next={() => setPage(page + 1)}
      hasMore={data.length < initialData.length}
      loader={<h4>Loading...</h4>}
    >
      {data.map((product) => (
        <Card key={product._id} data={product} />
      ))}
    </InfiniteScroll>
  );
};

const catToType = (cat) => {
  const lookup = {
    projects: "project",
    "team-members": "team",
    posts: "post",
    pursuits: "pursuit",
    videos: "video",
  };
  return lookup[cat];
};

const filterData = (data = [], categories) => {
  if (categories) {
    let categoriesArr = categories.split(",");
    categoriesArr = categoriesArr.map((cat) => catToType(cat));
    data = data.filter((i) => categoriesArr.includes(i._type));
  }
  return data || [];
};

export default function Search({ settings }) {
  const router = useRouter();
  const { q = "", categories = "" } = router.query || {};
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState(undefined);

  // eslint-disable-next-line no-unused-vars
  let { data: initialData, error } = useSWR(["/api/search", q], () =>
    search(q)
  );
  const isLoading = !initialData;
  const data = filterData(initialData, categories);
  if (!initialData) initialData = [];

  const categoriesArr = categories ? categories.split(",") : [];
  const filters = [
    {
      id: "filters",
      name: "FILTER:",
      options: [
        {
          value: "projects",
          checked: categoriesArr.includes("projects"),
          label: `Projects (${
            initialData.filter((i) => i._type === "project").length
          })`,
        },
        {
          value: "team-members",
          checked: categoriesArr.includes("team-members"),
          label: `Team Members (${
            initialData.filter((i) => i._type === "team").length
          })`,
        },
        {
          value: "posts",
          checked: categoriesArr.includes("posts"),
          label: `Blog Posts (${
            initialData.filter((i) => i._type === "post").length
          })`,
        },
        {
          value: "pursuits",
          checked: categoriesArr.includes("pursuits"),
          label: `Pursuits (${
            initialData.filter((i) => i._type === "pursuit").length
          })`,
        },
        {
          value: "videos",
          checked: categoriesArr.includes("videos"),
          label: `Videos (${
            initialData.filter((i) => i._type === "video").length
          })`,
        },
        // { value: "live-cams", label: "Live Cams" },
      ],
    },
  ];

  const changeHandler = (event) => {
    const query = event.target.value;
    setQuery(query || "");
  };

  const debouncedChangeHandler = useMemo(() => {
    return debounce(changeHandler, 300);
  }, []);

  useEffect(() => {
    if (query !== undefined && query !== q) {
      handleFilterChanged({ query });
    }
  }, [query]);

  const handleFilterChanged = (e) => {
    const { query: initialQuery } = e;
    const { value, checked } = e.target || {};

    let categoriesQueryParamsStr = "";
    const categoriesArr = categories ? categories.split(",") : [];
    if (!initialQuery) {
      if (!checked && categoriesArr.includes(value)) {
        const index = categoriesArr.findIndex((i) => i === value);
        categoriesArr.splice(index, 1);
      } else if (checked && !categoriesArr.includes(value)) {
        categoriesArr.push(value);
      }
    }
    categoriesQueryParamsStr = categoriesArr.join(",");
    let queryParamsStr = "";
    if (categoriesQueryParamsStr) {
      queryParamsStr = `?categories=${categoriesQueryParamsStr}`;
    }
    let _query = initialQuery;
    if (q && (_query === undefined || _query === null)) _query = q;
    if (_query) {
      const symbol = queryParamsStr ? "&" : "?";
      queryParamsStr = `${queryParamsStr}${symbol}q=${_query}`;
    }

    setPage(1);
    router.replace(`/search${queryParamsStr}`, undefined, {
      scroll: true,
      shallow: true,
    });
  };

  return (
    <>
      <Meta path="/search" {...settings.seo} />

      <div className="bg-white">
        <div>
          {/* Mobile filter dialog */}
          <Transition.Root show={mobileFiltersOpen} as={Fragment}>
            <Dialog
              as="div"
              className="fixed inset-0 flex z-40 lg:hidden"
              onClose={setMobileFiltersOpen}
            >
              <Transition.Child
                as={Fragment}
                enter="transition-opacity ease-linear duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-25" />
              </Transition.Child>

              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <div className="ml-auto relative max-w-xs w-full h-full bg-white shadow-xl py-4 pb-6 flex flex-col overflow-y-auto">
                  <div className="px-4 flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                      Filters
                    </h2>
                    <button
                      type="button"
                      className="-mr-2 w-10 h-10 p-2 flex items-center justify-center text-gray-400 hover:text-gray-500"
                      onClick={() => setMobileFiltersOpen(false)}
                    >
                      <span className="sr-only">Close menu</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  {/* Filters */}
                  <form className="mt-4">
                    {filters.map((section) => (
                      <div
                        as="div"
                        key={section.name}
                        className="border-t border-gray-200 pt-4 pb-4"
                      >
                        <div className="space-y-6 pt-4 pb-2 px-4">
                          {section.options.map((option, optionIdx) => (
                            <div
                              key={option.value}
                              className="flex items-center"
                            >
                              <input
                                id={`${section.id}-${optionIdx}-mobile`}
                                name={`${section.id}[]`}
                                defaultValue={option.value}
                                type="checkbox"
                                defaultChecked={option.checked}
                                onInput={handleFilterChanged}
                                className="h-4 w-4 border-gray-300 rounded text-accent-600 focus:ring-accent-500"
                              />
                              <label
                                htmlFor={`${section.id}-${optionIdx}-mobile`}
                                className="ml-3 text-sm text-gray-500"
                              >
                                {option.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </form>
                </div>
              </Transition.Child>
            </Dialog>
          </Transition.Root>

          <main className="max-w-2xl mx-auto pb-16 px-4 sm:px-6 lg:max-w-7xl lg:px-8">
            <div className="pt-10 lg:grid lg:grid-cols-3 lg:gap-x-8 xl:grid-cols-4">
              <aside>
                <h2 className="sr-only">Filters</h2>

                <button
                  type="button"
                  className="inline-flex items-center lg:hidden"
                  onClick={() => setMobileFiltersOpen(true)}
                >
                  <span className="text-sm font-medium text-gray-700">
                    Filters
                  </span>
                  <PlusSmIcon
                    className="flex-shrink-0 ml-1 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </button>

                <div className="hidden sticky top-5 lg:block">
                  <form className="divide-y divide-gray-200 space-y-10">
                    {filters.map((section, sectionIdx) => (
                      <div
                        key={section.name}
                        className={sectionIdx === 0 ? null : "pt-10"}
                      >
                        <fieldset>
                          <legend className="block text-sm font-medium text-gray-900">
                            {section.name}
                          </legend>
                          <div className="pt-6 space-y-3">
                            {section.options.map((option, optionIdx) => (
                              <div
                                key={`${option.value}-${option.checked}`}
                                className="flex items-center"
                              >
                                <input
                                  id={`${section.id}-${optionIdx}`}
                                  name={`${section.id}[]`}
                                  defaultValue={option.value}
                                  defaultChecked={option.checked}
                                  type="checkbox"
                                  onInput={handleFilterChanged}
                                  className="h-4 w-4 border-gray-300 rounded text-accent-600 focus:ring-accent-500"
                                />
                                <label
                                  htmlFor={`${section.id}-${optionIdx}`}
                                  className="ml-3 text-sm text-gray-600"
                                >
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </fieldset>
                      </div>
                    ))}
                  </form>
                </div>
              </aside>

              {/* Product grid */}
              <div className="mt-6 lg:mt-0 lg:col-span-2 xl:col-span-3">
                <SearchInput
                  initialValue={q}
                  debouncedChangeHandler={debouncedChangeHandler}
                />
                <div className="mt-6">
                  <LazyList data={data} setPage={setPage} page={page} />

                  {isLoading ? <Spinner /> : null}

                  {!data || (!data.length && !isLoading) ? (
                    <div className="p-6 h-64 flex flex-col items-center justify-center">
                      <div className="max-w-sm mx-auto text-center">
                        <div className="text-lg leading-6 font-medium text-gray-900">
                          No results found
                        </div>
                        <div className="text-sm text-gray-500 mt-2">
                          We couldn’t find any results that match your filters.
                          Try a different filter or{" "}
                          <button
                            onClick={() => {
                              router.replace("/search", undefined, {
                                scroll: false,
                                shallow: true,
                              });
                            }}
                            className="underline rounded focus:outline-none hover:underline text-gray-800 hover:text-gray-900 focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 transition"
                          >
                            reset all filters
                          </button>
                          .
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}

export async function getStaticProps() {
  const settings = await getSettingsForPage("search");

  return {
    props: { settings },
    revalidate: 60,
  };
}
